<template>
  <div
    :id="pageId"
    :class="pageId"
    class="page-content"
  >
    <div class="mb-20">
      <div
        v-if="isScheduledRideRestricted"
        class="alert alert-danger scheduled-ride-restriction-alert"
      >
        This partner does not allow scheduling. Please read the Subsidy notes and contact your TL with questions.
        {{ customerInfo?.partner?.config?.allowWillCallRides ? 'Will Call rides are allowed.' : '' }}
      </div>
      <p>
        <b>⚠️ NOTE:</b>
        All scheduled rides will start looking for a driver 10 mins before the scheduled time. This is because it takes some time for the
        car to get to the actual destination.
      </p>
    </div>

    <div class="card">
      <div class="card-header">
        <h3 class="card-title">
          {{ formTitle }}
        </h3>
      </div>

      <hr class="card-title-line">

      <div class="card-content">
        <customized-form @submit="submitForm">
          <div class="scheduler-content">
            <div
              v-if="isBusinessAccount"
              class="row"
            >
              <label class="col-sm-3 control-label mt-10"> Custom Rider Name* </label>
              <div class="col-sm-8">
                <sanitized-input
                  id="customRiderName"
                  v-model="rideData.customRiderName"
                  placeholder="Please input rider name"
                  required
                />
                <span class="help-block text-red"> This is a business account, please ask the caller for the rider's name </span>
              </div>
            </div>

            <div class="row align-items-center">
              <label class="col-sm-3 control-label"> Phone Number* </label>
              <phone-number-input
                v-model="rideData.phoneNumber"
                :disabled="isPhoneNumberFieldDisabled"
                class="col-sm-8"
              />
            </div>

            <template v-if="isGeneralRideType">
              <div class="row">
                <label class="col-sm-3 control-label pt-5 mobile-pt-0"> Preferred Contact Number* </label>
                <phone-number-input
                  id="contact-phone"
                  v-model="rideData.riderPhone"
                  :page-id="pageId"
                  class="col-sm-8"
                >
                  <template #help-block>
                    <span
                      v-if="status.preferredContactWarning"
                      class="error-message"
                    >
                      {{ status.preferredContactWarning }}
                    </span>
                    <br v-if="status.preferredContactWarning">
                    <span
                      v-if="isBusinessAccount"
                      class="text-red"
                    >
                      This is a business account, please ask the caller for the rider's phone number
                    </span>
                    <span v-else>
                      If the customer is at a friend's house for example, you can put the friend's home number here and the system will use
                      this number as the contact number.
                    </span>
                  </template>
                </phone-number-input>
              </div>

              <div class="row">
                <label class="col-sm-3 control-label pt-10"> Trip Name* </label>
                <div class="col-sm-8">
                  <sanitized-input
                    v-model="rideData.rideName"
                    placeholder="Give a name to this trip so you can easily identify it."
                    required
                  />
                </div>
              </div>
            </template>

            <div
              v-if="availablePartnerRidePurposes.length > 0"
              class="row align-items-center"
            >
              <label class="col-sm-3 control-label"> Ride Purpose* </label>
              <div class="col-sm-8">
                <dropdown-list
                  v-model="rideData.partnerRidePurpose"
                  :disabled="isAfterGettingEstimation"
                  :options="availablePartnerRidePurposes"
                  required
                />
              </div>
            </div>

            <div class="row align-items-center">
              <label class="col-sm-3 control-label"> Type of Car* </label>
              <div class="col-sm-8">
                <dropdown-list
                  v-model="rideData.rideType"
                  :disabled="isAfterGettingEstimation"
                  :options="availableRideTypes"
                  required
                />
              </div>
            </div>

            <StawavRideData
              v-if="isStawavRideType"
              :data="rideData"
              :disabled-date="disabledDate"
              :is-disabled-fields="isAfterGettingEstimation"
              :is-show-time-warning-message="status.isShowTimeWarningMessage"
              @change-pickup-time="showLookingDriverBefore10MinsPopup"
            />

            <LocalTaxiRideData
              v-if="isLocalTaxiRideType"
              :data="localTaxiRideData"
              :disabled-date="disabledDate"
              @set-local-taxi-disabled-state="setLocalTaxiDisabledState"
            />

            <div
              v-if="isGeneralRideType"
              class="row align-items-center"
            >
              <label class="col-sm-3 control-label"> Service Type </label>
              <div class="col-sm-8">
                <dropdown-list
                  v-model="rideData.serviceType"
                  :disabled="isAfterGettingEstimation"
                  :options="serviceTypeOptions"
                />
              </div>
            </div>

            <template v-if="isGeneralRideType">
              <div class="row align-items-center">
                <el-tooltip
                  class="item"
                  effect="dark"
                  placement="bottom"
                  popper-class="custom-tooltip auto-retry"
                >
                  <template #content>
                    This will send a scheduled reminder call to the customer before they are picked up.<br>
                    <span>Example:</span> they want to receive a reminder call for their scheduled trip 60 minutes before their pick up time
                    so they can start getting ready
                  </template>

                  <label class="col-sm-3 control-label"> Additional Reminder Call Sent at (minutes) </label>
                </el-tooltip>
                <div class="col-sm-6 d-flex align-items-center">
                  <number-input
                    id="minutesBeforeCustomReminder"
                    v-model="rideData.minutesBeforeCustomReminder"
                    :is-decimal="false"
                    class="form-control"
                    max="300"
                    min="5"
                    type="number"
                  />
                  &nbsp;&nbsp;minutes
                </div>
              </div>

              <div class="row align-items-center">
                <el-tooltip
                  class="item"
                  effect="dark"
                  placement="bottom"
                  popper-class="custom-tooltip auto-retry"
                >
                  <template #content>
                    This will send a confirmation call to the customer right after creating an order.<br>
                    <span>Example:</span> they want to receive a confirmation call for their scheduled trip 10 minutes after creating an
                    order so they can be sure that ride is actually scheduled
                  </template>

                  <label class="col-sm-3 control-label">Confirmation Call Sent After Order Creation (minutes)</label>
                </el-tooltip>
                <div class="col-sm-6 d-flex flex-column">
                  <div class="d-flex align-items-center">
                    <number-input
                      id="minutesAfterCreationCustomReminder"
                      ref="scheduledRideReminderInputElement"
                      v-model="rideData.minutesAfterCreationCustomReminder"
                      :allow-null="true"
                      :is-decimal="false"
                      :disabled="!!scheduledRideId"
                      class="form-control"
                      type="number"
                      max="480"
                      min="0"
                      @blur="validateScheduledRideReminderMinutesAfterCreation"
                    />
                    &nbsp;&nbsp;minutes
                  </div>
                  <span
                    v-if="!!invalidErrorMessage"
                    class="help-block text-red"
                  >
                    {{ invalidErrorMessage }}
                  </span>
                </div>
              </div>

              <div class="row">
                <div class="col-sm-5 offset-sm-3">
                  <div>
                    <el-checkbox
                      id="isExpandDriverSearch"
                      v-model="rideData.isExpandDriverSearch"
                    >
                      Expand Driver Search
                    </el-checkbox>
                  </div>
                </div>
              </div>

              <div
                v-if="isAllowedToExpandVehicleSearch"
                class="row"
              >
                <div class="col-sm-5 offset-sm-3">
                  <div>
                    <el-checkbox
                      id="isExpandVehicleSearch"
                      v-model="rideData.isExpandVehicleSearch"
                    >
                      Expand Vehicle Search
                    </el-checkbox>
                  </div>
                </div>
              </div>

              <ChooseScheduledRideType
                v-model="rideData.scheduleKind"
                :scheduled-ride-id="scheduledRideId"
              />

              <RepeatRideData
                v-if="rideData.scheduleKind === 'multi'"
                :data="rideData"
                :disabled-date="disabledDate"
                :is-show-time-warning-message="status.isShowTimeWarningMessage"
                @change-pickup-time="showLookingDriverBefore10MinsPopup"
              />

              <SingleRideData
                v-if="rideData.scheduleKind === 'single'"
                :data="rideData"
                :disabled-date="disabledDate"
                :is-show-time-warning-message="status.isShowTimeWarningMessage"
                @change-pickup-time="showLookingDriverBefore10MinsPopup"
              />

              <WillcallRideData
                v-if="rideData.scheduleKind === 'willcall'"
                :data="rideData"
                :disabled-date="disabledDate"
              />

              <AppointmentRideData
                v-if="rideData.scheduleKind === 'appointment'"
                :data="rideData"
                :disabled-date="disabledDate"
                :is-show-time-warning-message="status.isShowTimeWarningMessage"
                @change-pickup-time="showLookingDriverBefore10MinsPopup"
              />
            </template>

            <ordering-address
              ref="startAddressComponent"
              v-model="startAddress"
              :anchor-bound="endAddressAnchorBound"
              :index="0"
              :is-disabled="isAfterGettingEstimation"
              :is-location-name-dialog-show="true"
              :is-location-name-show="!!isStawavRideType || rideData.scheduleKind === 'willcall'"
              :is-phone-number-valid="isPhoneNumberValid"
              :is-venues-search="isGeneralRideType"
              :ride-type="rideData.rideType"
              :show100-miles-modal="true"
              :venues="pickupVenues"
              is-geocode-show
              is-show-save-location
              map-pin-type="pickup"
              page-id="start-address"
              @swap="swapAddress"
              @venueNote="setVenueNote"
              @set-venue-places="setVenuePlaces"
              @change-marker-position="setMarkerPosition"
            >
              <template #title>
                Start Address*
              </template>
            </ordering-address>

            <ordering-address
              ref="endAddressComponent"
              v-model="endAddress"
              :anchor-bound="startAddressAnchorBound"
              :class="{ 'mb-20': !isGeneralRideType }"
              :index="1"
              :is-disabled="isAfterGettingEstimation"
              :is-location-name-show="!!isStawavRideType || rideData.scheduleKind === 'willcall'"
              :is-phone-number-valid="isPhoneNumberValid"
              :is-venues-search="isGeneralRideType"
              :ride-type="rideData.rideType"
              :additionalPins="[markers[0]]"
              :venues="dropoffVenues"
              class="mt-10"
              is-geocode-show
              is-show-save-location
              map-pin-type="dropoff"
              page-id="end-address"
              @swap="swapAddress"
              @venueNote="setVenueNote"
              @set-venue-places="setVenuePlaces"
              @change-marker-position="setMarkerPosition"
            >
              <template #title>
                End Address*
              </template>
            </ordering-address>

            <template v-if="isGeneralRideType">
              <template v-if="isTransportation">
                <div class="row mt-30">
                  <label class="col-sm-4 control-label pt-1"> What Are You Wearing Today? </label>
                  <div class="col-sm-7">
                    <sanitized-input
                      v-model="riderClothes"
                      placeholder="Describe clothing"
                    />
                  </div>
                </div>

                <div class="row">
                  <label class="col-sm-4 control-label pt-1"> How Should the Driver Announce Their Arrival?* </label>
                  <div class="col-sm-7">
                    <dropdown-list
                      v-model="driverArrival"
                      :options="driverArrivals"
                      required
                    />
                  </div>
                </div>

                <div class="row">
                  <PButton
                    style="margin: auto;"
                    type="purple"
                    round
                    @click="showAccessOptionsPopupDialog"
                  >
                    Edit Accessibility Options
                  </PButton>
                </div>

                <div class="row">
                  <div class="col-sm-8 offset-sm-3">
                    <div>
                      <el-checkbox
                        id="noteGroceries"
                        v-model="hasGroceries"
                      >
                        The rider has groceries.
                      </el-checkbox>
                    </div>
                    <div>
                      <el-checkbox
                        id="noteLuggage"
                        v-model="hasLuggage"
                        class="pb-0"
                      >
                        The rider has luggage.
                      </el-checkbox>
                    </div>
                  </div>
                </div>

                <div class="row mt-30">
                  <DriverAcceptedTextMessageNote
                    v-model="rideData.noteToDriver"
                    :driverNotePickUp="driverNotePickUp"
                    :driverNoteDropOff="driverNoteDropOff"
                    :serviceType="rideData.serviceType"
                    :customRiderName="rideData.customRiderName"
                    :isEmergency="false"
                    :startAddress="startAddress"
                    :infoForDriver="customerInfo.infoForDriver"
                    :callerId="customerInfo.id"
                  />
                </div>
              </template>

              <div
                :class="{ 'mt-30': !isTransportation }"
                class="row"
              >
                <div class="col-sm-8 offset-sm-3">
                  <el-checkbox
                    id="isNeededToMonitorRide"
                    v-model="isNeededToMonitorRide"
                  >
                    Have RSS monitor this ride
                  </el-checkbox>
                </div>
              </div>

              <div
                v-if="isNeededToMonitorRide"
                class="row"
              >
                <label class="col-sm-3 control-label pt-5 mobile-pt-10">Reason for monitoring this ride..*</label>
                <div class="col-sm-8">
                  <sanitized-textarea
                    v-model="rideData.notesForLP"
                    required
                    autosize
                  />
                </div>
              </div>

              <template v-if="isTransportation">
                <template v-if="shouldDisplaySurge">
                  <div class="row mb-0">
                    <label class="col-sm-3 control-label"> Confirm Surge* </label>
                    <div class="col-sm-8">
                      <div class="pb-10">
                        <el-radio
                          v-model="rideData.callToConfirmSurge"
                          label="yes"
                        >
                          Call to confirm surge
                        </el-radio>
                      </div>

                      <div class="pb-10">
                        <el-radio
                          v-model="rideData.callToConfirmSurge"
                          label="no"
                        >
                          Don’t call to confirm surge
                        </el-radio>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="offset-sm-3 col-sm-8 col-12">
                      If a scheduled ride has surge pricing (higher price than usual) then it will call the user to confirm the price before
                      the car gets sent out. However, if "IF THERE IS SURGE PRICING, CALL TO CONFIRM?" checkbox is not checked then a car
                      will get sent automatically no matter what the price is.
                    </div>
                  </div>
                </template>

                <div class="row">
                  <label class="col-sm-3 control-label" />
                  <div class="col-sm-7">
                    <el-checkbox
                      id="isDoNotApplySubsidy"
                      v-model="rideData.isDoNotApplySubsidy"
                    >
                      Do not apply partner subsidy to this ride(s)
                    </el-checkbox>
                    <div
                      class="help-block"
                      style="padding: 0 0 0 35px"
                    >
                      This option should only be selected if the customer wants to pay for the full amount of the ride and does not want it
                      to count towards their subsidized rides.
                    </div>
                  </div>
                </div>
                <div class="row">
                  <label class="col-sm-3 control-label" />
                  <div class="col-sm-7">
                    <el-checkbox
                      id="isDoNotApplyExpiringCredits"
                      v-model="rideData.isDoNotApplyExpiringCredits"
                    >
                      Do not apply expiring credits to this ride(s)
                    </el-checkbox>
                  </div>
                </div>
              </template>
            </template>
          </div>

          <hr>
          <SimilarScheduledRide
            v-if="similarScheduledRide"
            :scheduled-ride="similarScheduledRide"
          />

          <template v-if="isAfterGettingEstimation">
            <StawavEstimation
              v-if="status.isShowStawavEstimation"
              :estimation="stawavEstimation"
              :is-one-way-trip="!rideData.returnTime"
            />

            <FresnoEstimation
              v-else-if="status.isShowFresnoEstimation"
              :estimation="fresnoEstimation"
            />

            <GreenPointEstimation
              v-else-if="status.isShowGreenPointEstimation"
              :estimation="fresnoEstimation"
            />

            <template v-else>
              <div class="partner-estimate-header">
                <div class="partner-estimate-tabs">
                  <button
                    v-for="(tab, index) in partnerEstimateTabs"
                    :key="tab.key"
                    type="button"
                    class="partner-estimate-tab"
                    :class="{ 'partner-estimate-tab--active': selectedPartnerEstimateIndex === index }"
                    @click="selectPartnerEstimateTab(index)"
                  >
                    {{ tab.label }}
                  </button>
                </div>

                <div
                  v-if="selectedPartnerEstimate"
                  class="partner-estimate-info"
                >
                  <div class="partner-estimate-info-field">
                    <label class="partner-estimate-info-label">Partner Name</label>
                    <div class="partner-estimate-info-value">
                      {{ selectedPartnerEstimate.partnerName || selectedPartnerEstimate.name || '—' }}
                    </div>
                  </div>
                </div>
              </div>

              <EstimationRide
                v-model:operatorConfirmedSurgePriceWithCustomer="operatorConfirmedSurgePriceWithCustomer"
                :estimation-ride="estimationRide"
                :show-full-detail="!isHideRideEstimateFullDetail"
                :is-scheduled-ride="true"
              />
            </template>

            <div class="form-submit-button-group">
              <hr>
              <p-button
                :disabled="isSubmitBtnDisabled"
                round
                type="purple"
                @click="submitForm()"
              >
                {{ submitBtnText }}
              </p-button>

              <p-button
                round
                type="yellow"
                @click="cancelEstimation()"
              >
                Cancel
              </p-button>
            </div>

            <BasicRideLimitValueUpgradeBanner class="mt-3" />
          </template>

          <message-html
            :has-top-line="false"
            :result-html="status.resultHtml"
            has-bottom-line
          />

          <div
            v-if="!isAfterGettingEstimation"
            class="get-estimation"
          >
            <div class="form-submit-button-group">
              <p-button
                v-if="isGetEstimateNeeded"
                :disabled="isSubmitBtnDisabled"
                native-type="submit"
                round
                type="purple"
              >
                {{ estimationBtnText }}
              </p-button>
              <p-button
                v-else
                :disabled="isSubmitBtnDisabled"
                round
                type="purple"
                @click="submitForm()"
              >
                {{ submitBtnText }}
              </p-button>

              <p-button
                round
                type="yellow"
                @click="resetForm"
              >
                Reset
              </p-button>
            </div>

            <BasicRideLimitValueUpgradeBanner class="mt-3" />
          </div>
        </customized-form>
      </div>
    </div>

    <AddAccessibilityOptionsDialog
      :customerInfo="customerInfo"
      :isShowDialog="status.isShowAccessibilityPopupDialog"
      @cancel="status.isShowAccessibilityPopupDialog = false"
      @submit="saveAccessibilityOptionsPopupResponse"
    />
  </div>
</template>

<script lang="ts">
import { ref, Ref, ShallowRef, toRefs, reactive, computed, watch, onMounted, onBeforeUnmount, ComponentPublicInstance, shallowRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Moment from 'moment-timezone';
import _ from 'lodash';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import rideUtility from '@/Utils/rideUtility';
import { getSelectedEntranceCoordinates } from '@/Utils/partners/masonic';
import { sendGet, sendPost, sendDelete } from '@/Utils/api';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { useReturnTripAlert } from '@/use/returnTripAlert';
import { useAddress } from '@/use/address';
import { usePartner } from '@/use/partner';
import { useRide } from '@/use/ride';
import { useRideTypes } from '@/use/rideTypes';
import { useScheduledRide } from '@/use/scheduledRide';
import { defaultTimeZone, genderList } from '@/constants/app';
import { stawavLevelOfServices } from '@/constants/ride';
import { CustomLocationType, AddressType } from '@/models/address';
import { ScheduledRideInput, ScheduledRide } from '@/models/scheduledRide';
import { MedicalTripResult, RideServiceType } from '@/models/ride';
import { CallerAdditionalInfo } from '@/models/caller';
import { ListType } from '@/models/app';
import type { Venue, VenueSelection } from '@/models/venue';
import { scheduledRideService } from '@/services/scheduledRide';
import { callerService } from '@/services/caller';
import { rideService } from '@/services/ride';
import { canOfferNashDelivery } from '@/services/operators/viewModel';

import EstimationRide from '@/components/UIComponents/OrderingForms/EstimationRide.vue';
import SimilarScheduledRide from '@/components/UIComponents/OrderingForms/SimilarScheduledRide.vue';
import LocalTaxiRideData from './LocalTaxiRideData.vue';
import StawavRideData from './StawavRideData.vue';
import StawavEstimation from './StawavEstimation.vue';
import RepeatRideData from './RepeatRideData.vue';
import SingleRideData from './SingleRideData.vue';
import WillcallRideData from './WillcallRideData.vue';
import AppointmentRideData from './AppointmentRideData.vue';
import ChooseScheduledRideType from './ChooseScheduledRideType.vue';
import FresnoEstimation from './FresnoEstimation.vue';
import GreenPointEstimation from './GreenPointEstimation.vue';
import DriverAcceptedTextMessageNote from '@/components/Main/Operator/RideOrdering/DriverAcceptedTextMessageNote.vue';
import AddAccessibilityOptionsDialog from '@/components/Main/Dialogs/AddAccessibilityOptions.vue';
import BasicRideLimitValueUpgradeBanner from '@/components/Main/Operator/RideOrdering/BasicRideLimitValueUpgradeBanner.vue';

const pageId = 'ride-scheduler';
const similarScheduledRideLimitDurationMins = 20;
const unknownNameList = ['na', 'n/a', 'unknown'];
const FRESNO_ADDITION_DISTANCE_RATE = 3;
const FRESNO_DISTANCE_LIMIT = 10;

export default {
  name: 'RideScheduler',

  components: {
    EstimationRide,
    SimilarScheduledRide,
    LocalTaxiRideData,
    StawavRideData,
    StawavEstimation,
    RepeatRideData,
    SingleRideData,
    WillcallRideData,
    ChooseScheduledRideType,
    FresnoEstimation,
    GreenPointEstimation,
    AppointmentRideData,
    DriverAcceptedTextMessageNote,
    AddAccessibilityOptionsDialog,
    BasicRideLimitValueUpgradeBanner,
  },

  setup: () => {
    const route = useRoute();
    const router = useRouter();

    const invalidErrorMessage = ref('');
    const scheduledRideReminderInputElement: Ref<ComponentPublicInstance | null> = ref(null);

    const {
      customerPhoneNumber,
      customerInfo,
      customerStatus,
      customerPartner,
      customerPartnerCode,
      customerPartnerId,
      isScheduledRideRestricted,
      availablePartnerRidePurposes,
      rideAvailablePhoneNumber,
      isHideRideEstimateFullDetail,
      isAllowedToExpandVehicleSearch,
      setCustomerInfo,
    } = useCustomer();

    const { isBusinessAccount } = toRefs(customerStatus);
    const {
      trimAddress,
      getStreetAddressByType,
      updateAddressWithGeocode,
      getFormattedAddress,
      getAddressObjectFromString,
      getDistance,
      getAnchorBound,
    } = useAddress();
    const {
      showConfirmPopupDialog,
      showErrorPopupDialog,
      showSuccessPopupDialog,
      showWarningPopupDialog,
      showConfirmPopupDialogWithValidation,
    } = useSwalDialog();
    const { showReturnTripAlertIfNeeded } = useReturnTripAlert();
    const { partnerId, partnerCode } = usePartner();
    const {
      availableServiceTypeList,
      duplicatedScheduledRide,
      rideOrderData,
      setRideOrderData,
      setDuplicatedScheduledRide,
      checkPharmacyQuestions,
      checkSpecialRequestQuestions,
      checkMedicalTripForDutchesPartnership,
      checkMedicalTripForDiamondBarPartnership,
      checkMedicalTripForRotcPartnership,
      showLowDriverAvailabilityMessage,
    } = useRide();

    const { getScheduledRideTypes, findPartnerLocalTaxiRideType, getAvailableRideType } = useRideTypes();

    const rideData: Ref<any> = ref({
      customRiderName: '',
      phoneNumber: '',
      riderPhone: '',
      callToConfirmSurge: '',
      rideName: '',
      partnerRidePurpose: '',
      rideType: '',
      serviceType: RideServiceType.Transportation,
      scheduleKind: 'single',
      rideMultiple: false,
      isWillCall: false,
      pickupDate: null,
      pickupTime: null,
      scheduledAppointmentDate: null,
      scheduledAppointmentTime: null,
      scheduledAppointmentBufferMinutes: 10,
      repeatEveryXWeek: 1,
      day0: false,
      day1: false,
      day2: false,
      day3: false,
      day4: false,
      day5: false,
      day6: false,
      endOn: null,
      endOnType: 'never',
      endAfterXTimes: 0,
      noteToDriver: '',
      minutesBeforeCustomReminder: '',
      minutesAfterCreationCustomReminder: customerPartner?.value?.callOptions?.scheduledRideReminderMinutesAfterCreation || '',
      isDoNotApplySubsidy: false,
      isDoNotApplyExpiringCredits: false,
      overrideBigCar: false,
      overrideSmallCar: false,
      overrideBannedCar: false,
      overrideBadDriver: false,
      isExpandDriverSearch: false,
      isExpandVehicleSearch: false,
      notesForLP: '',
      notesForSpecialRequestOrPharmacy: '',
      gender: genderList[0].key,
      levelOfService: stawavLevelOfServices[0].key,
      hasOwnWheelchair: false,
      specialInstructions: '',
      numberOfCompanions: 0,
      appointmentType: '',
      appointmentTime: '',
      returnTime: '',
      stairStepsForPU: 0,
      stairStepsForDO: 0,
      dropOffPhoneNumber: '',
      isMedicalTrip: false,
    });
    const isNeededToMonitorRide = ref(false);

    let rideDataOriginal = shallowRef<any>(null);
    const similarScheduledRide: Ref<ScheduledRide | null> = ref(null);
    const rideCaller: Ref<any> = ref(null);
    let startAddressOriginal = shallowRef<any>(null);
    const startAddress: Ref<CustomLocationType> = ref({
      streetAddress: '',
      streetAddress1: '',
      city: '',
      state: '',
      zip: '',
      name: '',
      latitude: null,
      longitude: null,
      type: false,
    });
    const pickupVenues: Ref<Venue[]> = ref([]);
    let endAddressOriginal = shallowRef<any>(null);
    const endAddress: Ref<CustomLocationType> = ref({
      streetAddress: '',
      streetAddress1: '',
      city: '',
      state: '',
      zip: '',
      name: '',
      latitude: null,
      longitude: null,
      type: false,
    });
    const dropoffVenues: Ref<Venue[]> = ref([]);

    const markers: Ref<any> = ref([
      {
        position: {
          lat: 0.0,
          lng: 0.0,
        },
        initialPosition: {
          lat: 0.0,
          lng: 0.0,
        },
        index: 0,
        isDraggable: false,
        isShow: false,
        icon: '/static/img/pickup_marker.png',
      },
      {
        position: {
          lat: 0.0,
          lng: 0.0,
        },
        initialPosition: {
          lat: 0.0,
          lng: 0.0,
        },
        index: 1,
        isDraggable: false,
        isShow: false,
        icon: '/static/img/dropoff_marker.png',
      },
    ]);
    const scheduledRideId = ref(0);
    const status = reactive({
      isLoading: false,
      isExistPhoneNumber: false,
      isShowTimeWarningMessage: false,
      resultHtml: '',
      preferredContactWarning: '',
      isShowLookingDriverBefore10MinsPopup: false,
      isShowStawavEstimation: false,
      isShowFresnoEstimation: false,
      isShowGreenPointEstimation: false,
      isShowNormalRideEstimation: false,
      isShowAccessibilityPopupDialog: false,
    });
    const estimationRide = ref({
      firstName: '',
      lastName: '',
      rideType: '',
      surgeFactor: '',
      eta: '',
      duration: '',
      distance: '',
      lowEstimate: '',
      highEstimate: '',
      callRideFromEstimateLink: '',
      credits: '',
      partnerSubsidy: '',
      partnerSubsidyEstimate: null,
      isNoReturnTrip: false,
      returnTripEtaMins: undefined,
      riderName: '',
      etaText: '',
      estimate: '',
      perMile: '',
      perMinute: '',
      baseFare: '',
      minimumFare: '',
      operatingFee: '',
      customerCostOverride: '',
      currency: '',
      surgeKey: '',
      surgeUrl: '',
      fareId: '',
      pricingExplanation: '',
      storedLongRideOrderDataKey: '',
      warningMessage: '',
      fulfillmentIndicator: '',
    });
    const defaultEstimateData: Ref<any | null> = ref(null);
    const allPartnerEstimates: Ref<any[]> = ref([]);
    const selectedPartnerEstimateIndex = ref(0);
    const partnerEstimateTabs = computed(() => [
      { key: 'no-partner', label: 'No partner' },
      ...allPartnerEstimates.value.map((partnerEstimate, index) => ({
        key: `partner-${partnerEstimate.partnerId ?? index}`,
        label: `Partner #${index + 1}`,
      })),
    ]);
    const selectedPartnerEstimate = computed(() => {
      if (selectedPartnerEstimateIndex.value <= 0) {
        return null;
      }
      return allPartnerEstimates.value[selectedPartnerEstimateIndex.value - 1] || null;
    });
    const selectedPartnerId = computed(() => selectedPartnerEstimate.value?.partnerId);
    const localTaxiRideData = ref({
      pickupDate: '',
      pickupTime: '',
      noteToTaxiCompany: '',
      returnTime: '',
    });
    const stawavEstimation = ref({
      wheelchairTransportationRate: 75,
      wheelchairTransportationStandardRate: 75,
      wheelchairTransportationSurchargeRate: 0,
      passengerTransportationRate: 60,
      passengerTransportationStandardRate: 60,
      passengerTransportationSurchargeRate: 0,
      waitTimeRate: 0,
    });

    const fresnoEstimation = ref({
      distance: 0,
      rate: 0,
    });

    const startAddressComponent: Ref<any> = ref({});
    const endAddressComponent: Ref<any> = ref({});

    const driverNotePickUp = ref('');
    const driverNoteDropOff = ref('');

    const pickupVenueNote = ref('');
    const riderClothes = ref('');
    const hasGroceries = ref(false);
    const hasLuggage = ref(false);
    const driverArrivals = computed<ListType[]>(() => {
      const name = rideData.value.customRiderName || customerInfo.value?.firstName || 'rider';
      return [
        { label: 'Call me', key: `Call ${name} upon arrival` },
        { label: 'Honk the horn', key: `Honk horn upon arrival` },
        { label: 'Exit car and meet me', key: `Exit car and meet ${name}` },
      ];
    });
    const driverArrival = ref(driverArrivals.value[0].key);

    const {
      isLocalTaxiRideType,
      isGeneralRideType,
      isTransportation,
      isStawavRideType,
      isRepeatedRide,
      estimationBtnText,
      submitBtnText,
      isPharmacyOrder,
      isSpecialRequestOrder,
      isFresnoEOCTaxi,
      isGreenPointTaxi,

      getStawavRideData,
      getSchedulePostData,
    } = useScheduledRide(rideData);

    const isLocalTaxiNoteDisabled = ref<boolean>(false);

    // Nash cannot fulfil scheduled orders, so the test group can only schedule transportation.
    const serviceTypeOptions = computed(() =>
      canOfferNashDelivery.value
        ? availableServiceTypeList.value.filter(({ key }: ListType) => key === RideServiceType.Transportation)
        : availableServiceTypeList.value,
    );

    const operatorConfirmedSurgePriceWithCustomer = ref(false);

    const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(rideData.value.phoneNumber));

    const validateScheduledRideReminderMinutesAfterCreation = () => {
      invalidErrorMessage.value = scheduledRideReminderInputElement.value?.$el?.validationMessage;
    };

    const isAfterGettingEstimation = computed(
      () =>
        status.isShowNormalRideEstimation ||
        status.isShowStawavEstimation ||
        status.isShowFresnoEstimation ||
        status.isShowGreenPointEstimation,
    );

    const isPhoneNumberFieldDisabled = computed(
      () => scheduledRideId.value > 0 || status.isExistPhoneNumber || isAfterGettingEstimation.value,
    );

    const isAlternatePhoneNumberValid = computed(() => util.isPhoneNumberValid(rideData.value.riderPhone));

    const isSubmitBtnDisabled = computed(() => {
      if (!isPhoneNumberValid.value || status.isLoading) {
        return true;
      }

      if (isLocalTaxiNoteDisabled.value) {
        return true;
      }

      if (!isGeneralRideType.value) {
        return false;
      }

      if (!isAlternatePhoneNumberValid.value && !isStawavRideType.value) {
        // Stawav ride, we don't have alternate phone number
        return true;
      }

      if (isTransportation.value && shouldDisplaySurge.value && !rideData.value.callToConfirmSurge) {
        return true;
      }

      return false;
    });

    const formTitle = computed(() => {
      return scheduledRideId.value ? `Update Scheduled Ride ${scheduledRideId.value}` : 'Create a New Scheduled Ride';
    });

    const preferredVendorRideTypes = computed(() => customerPartner.value?.preferredVendorRideTypes || []);
    const availableRideTypes = computed(() =>
      getScheduledRideTypes(customerPartnerCode.value || partnerCode.value, preferredVendorRideTypes.value),
    );

    const pickupDropoffNotes = computed(() => {
      return util.makePickupDropoffDriverNotes(driverNotePickUp.value, driverNoteDropOff.value);
    });

    const startAddressAnchorBound = computed(() => getAnchorBound(startAddress.value));
    const endAddressAnchorBound = computed(() => getAnchorBound(endAddress.value));

    const maxBirthDate = computed(() => Moment().subtract(18, 'years').format('YYYY-MM-DD'));

    const isVendorScheduledRide = computed(() => !rideData.value.rideMultiple && rideData.value.serviceType === RideServiceType.Transportation);
    const shouldDisplaySurge = computed(() => {
      if (isVendorScheduledRide.value) {
        return false;
      }

      return customerPartner.value?.shouldDisplaySurge ?? true;
    });

    const disabledDate = (date: Date) => {
      const dateLocalMoment = Moment(date);
      const startOfTodayDefaultTimeZoneMoment = Moment.tz(customerInfo.value.timeZone || defaultTimeZone).startOf('day');
      const startOfTodayLocalMoment = Moment(startOfTodayDefaultTimeZoneMoment.format('YYYY-MM-DD hh:mm'));
      return dateLocalMoment.isBefore(startOfTodayLocalMoment, 'day');
    };

    const swapAddress = () => {
      const tempVenues = _.cloneDeep(pickupVenues.value);
      pickupVenues.value = _.cloneDeep(dropoffVenues.value);
      dropoffVenues.value = _.cloneDeep(tempVenues);

      const temp = _.cloneDeep(startAddress.value);
      startAddress.value = _.cloneDeep(endAddress.value);
      endAddress.value = _.cloneDeep(temp);

      startAddressComponent.value?.initAddressForm();
      endAddressComponent.value?.initAddressForm();
    };

    const setVenuePlaces = (venues: Venue[], locationIndex: number) => {
      if (locationIndex === AddressType.PickUp) {
        pickupVenues.value = venues;
      } else {
        dropoffVenues.value = venues;
      }
    };

    const setVenueNote = (venueNote: string, venuesInstruction: string, locationIndex: number, _selection?: VenueSelection | null) => {
      if (locationIndex === AddressType.PickUp) {
        pickupVenueNote.value = venueNote;
        rideData.value.venuesInstruction = venuesInstruction;
      } else if (locationIndex === AddressType.DropOff) {
        driverNoteDropOff.value = venueNote ? '📝 Dropoff at ' + venueNote : '';
      }
    }

    const showAccessOptionsPopupDialog = () => {
      status.isShowAccessibilityPopupDialog = true;
    };

    const saveAccessibilityOptionsPopupResponse = async (data: CallerAdditionalInfo) => {
      status.isShowAccessibilityPopupDialog = false;

      status.resultHtml = '';
      status.isLoading = true;

      try {
        await callerService.updateCaller({
          callerId: customerInfo.value.id,
          data,
        });

        setCustomerInfo({
          ...customerInfo.value,
          ...data,
        });
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      } finally {
        status.isLoading = false;
      }
    };

    const setMarkerPosition = (
      lat: number | string | null,
      lng: number | string | null,
      index: number,
      isMarkerChanged: boolean = true,
      isAddressChanged: boolean = false,
    ) => {
      util.updateMarkerPosition(markers.value[index], lat, lng, isAddressChanged);

      if (isMarkerChanged) {
        if (index === 0) {
          startAddress.value = updateAddressWithGeocode(startAddress.value, lat, lng);
        } else if (index === 1) {
          endAddress.value = updateAddressWithGeocode(endAddress.value, lat, lng);
        }
      }
    };

    const getRideScheduler = async () => {
      const loading = ElLoading.service({
        lock: true,
        text: 'Loading Ride Scheduler...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      try {
        const response = await scheduledRideService.fetchScheduledRideRecords({
          scheduledRideId: scheduledRideId.value,
        });

        if (response.count > 0) {
          const data = response.rows[0];
          const data1 = rideUtility.getRideSchedulerData(data);

          startAddressOriginal.value = _.cloneDeep(data1.startAddress);
          startAddress.value = _.cloneDeep(startAddressOriginal.value);
          endAddressOriginal.value = _.cloneDeep(data1.endAddress);
          endAddress.value = _.cloneDeep(endAddressOriginal.value);
          rideDataOriginal.value = _.omit(data1, ['startAddress', 'endAddress']);
          rideData.value = _.cloneDeep(rideDataOriginal.value);
          isNeededToMonitorRide.value = !!rideData.value.notesForLP;
        }
      } catch (error) {
        status.resultHtml = util.getErrorResponse(error);
      }
      loading.close();
    };

    const getRideById = async (id: string) => {
      const loading = ElLoading.service({
        lock: true,
        text: 'Loading Ride Data...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });
      try {
        const response = await sendGet('rides', {
          type: 'ride-id',
          q: id,
        });

        const ride = response.data.rows[0];
        if (!ride) {
          loading.close();
          return;
        }

        startAddressOriginal.value = {
          ...getAddressObjectFromString(ride.startLocation),
          latitude: Number(ride.startLat),
          longitude: Number(ride.startLong),
        };
        startAddress.value = _.cloneDeep(startAddressOriginal.value);

        if (ride.endLocation || ride.destination) {
          endAddressOriginal.value = {
            ...getAddressObjectFromString(ride.endLocation || ride.destination),
            latitude: Number(ride.endLat || ride.destinationLat),
            longitude: Number(ride.endLong || ride.destinationLng),
          };
          endAddress.value = _.cloneDeep(endAddressOriginal.value);
        }

        rideData.value.rideType = ride.rideType;
        rideData.value.phoneNumber = rideAvailablePhoneNumber.value;
        rideData.value.riderPhone = ride.riderPhone;
      } catch (error) {
        console.log(error);
      }
      loading.close();
    };

    const getScheduledRidePostData = (): ScheduledRideInput => {
      let postData = getSchedulePostData({
        startAddress: startAddress.value,
        endAddress: endAddress.value,
        estimationRide: isAfterGettingEstimation.value ? estimationRide.value : undefined,
        partnerId: selectedPartnerId.value,
      }) as any;

      postData.notesForLP = isNeededToMonitorRide.value ? rideData.value.notesForLP || '' : '';
      postData.notesForSpecialRequestOrPharmacy = rideData.value.notesForSpecialRequestOrPharmacy;

      if (isTransportation.value) {
        postData = {
          ...postData,
          callToConfirmSurge: rideData.value.callToConfirmSurge === 'yes' ? 1 : 0,
          minutesBeforeCustomReminder:
            typeof rideData.value.minutesBeforeCustomReminder === 'number' ? rideData.value.minutesBeforeCustomReminder : null,
          minutesAfterCreationCustomReminder:
            typeof rideData.value.minutesAfterCreationCustomReminder === 'number'
              ? rideData.value.minutesAfterCreationCustomReminder
              : null,
          isDoNotApplySubsidy: rideData.value.isDoNotApplySubsidy ? Number(rideData.value.isDoNotApplySubsidy) : 0,
          isDoNotApplyExpiringCredits: rideData.value.isDoNotApplyExpiringCredits
            ? Number(rideData.value.isDoNotApplyExpiringCredits)
            : 0,
          overrideBigCar: rideData.value.overrideBigCar ? Number(rideData.value.overrideBigCar) : 0,
          overrideSmallCar: rideData.value.overrideSmallCar ? Number(rideData.value.overrideSmallCar) : 0,
          overrideBannedCar: rideData.value.overrideBannedCar ? Number(rideData.value.overrideBannedCar) : 0,
          overrideBadDriver: rideData.value.overrideBadDriver ? Number(rideData.value.overrideBadDriver) : 0,
        };
      }

      let noteToDriver = '';
      if (pickupDropoffNotes.value) {
        noteToDriver = `${pickupDropoffNotes.value}\n`;
      }

      if (rideData.value.noteToDriver) {
        noteToDriver += rideData.value.noteToDriver;
      }

      // if scheduled ride is for special-request or pharmacy, we should not send noteToDriver to order the ride.
      if (!isSpecialRequestOrder.value && !isPharmacyOrder.value) {
        postData.noteToDriver = noteToDriver;
      }

      if (isBusinessAccount.value) {
        postData.customRiderName = rideData.value.customRiderName;
      }

      return postData;
    };

    const checkServiceTypeQuestions = async () => {
      if (isTransportation.value) {
        return true;
      }

      // no need to check questions if we asked the questions
      if (rideData.value.notesForSpecialRequestOrPharmacy) {
        return true;
      }

      const response = isSpecialRequestOrder.value
        ? await checkSpecialRequestQuestions(false)
        : await checkPharmacyQuestions(false);

      if (!response) {
        return false;
      }

      rideData.value.notesForSpecialRequestOrPharmacy = response.notes;
      if (response.rideType) {
        rideData.value.rideType = response.rideType;
      }

      return true;
    };

    const checkAndUpdateCustomerBirthday = async () => {
      if (isTransportation.value) {
        return true;
      }

      // no need to check birthday if there is no customer info b/c it is just for getting estimation
      if (!rideCaller.value.id) {
        return true;
      }

      if (rideCaller.value.dob) {
        return true;
      }

      const birthday = await showConfirmPopupDialogWithValidation({
        title: 'Please confirm the birthday with customer',
        input: 'date',
        inputPlaceholder: 'Please input birthday',
        validationText: 'Please input birthday',
        confirmButtonText: 'Confirm',
        dateValidation: {
          maxDate: maxBirthDate.value,
        },
      });

      if (!birthday) {
        return false;
      }

      try {
        await callerService.updateCaller({
          callerId: rideCaller.value.id,
          data: {
            dob: birthday,
          },
        });

        if (customerInfo.value.id) {
          setCustomerInfo({
            ...customerInfo.value,
            dob: birthday,
          });
        }

        return true;
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }

      return false;
    };

    const checkMedicalTripForPartnerships = async (isLocalTaxiRideType: boolean = false) => {
      rideData.value.isMedicalTrip = false;
      status.isLoading = true;

      const loading = ElLoading.service({
        lock: true,
        text: 'Checking Medical Trip for Partnership...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      const resultDutchesPartnership = await checkMedicalTripForDutchesPartnership(startAddress.value, endAddress.value);
      const resultDiamondBarPartnership = await checkMedicalTripForDiamondBarPartnership(startAddress.value, endAddress.value);
      const resultRotcPartnership = await checkMedicalTripForRotcPartnership(startAddress.value, endAddress.value);

      const results = [resultDutchesPartnership, resultDiamondBarPartnership, resultRotcPartnership];

      const isValidationFailed = results.some((result) => result === MedicalTripResult.VALIDATION_FAILED);
      const isUserDeclined = results.some((result) => result === MedicalTripResult.USER_DECLINED);
      const isValidationPassed = results.some((result) => result === MedicalTripResult.VALIDATION_PASSED);
      const isNoMedicalPlace = results.some((result) => result === MedicalTripResult.NO_MEDICAL_PLACE);

      status.isLoading = false;
      loading.close();

      if (isLocalTaxiRideType) {
        if (isValidationFailed || isUserDeclined || isNoMedicalPlace) {
          return false;
        }

        if (isValidationPassed) {
          rideData.value.isMedicalTrip = true;
        }
      } else {
        if (isValidationFailed) {
          return false;
        }

        if (isUserDeclined || isNoMedicalPlace) {
          rideData.value.isDoNotApplySubsidy = true;
        }

        if (isValidationPassed) {
          rideData.value.isMedicalTrip = true;
        }
      }

      return true;
    };

    const submitForm = async () => {
      if (isNeededToMonitorRide.value && !rideData.value.notesForLP?.trim()) {
        showErrorPopupDialog({
          text: 'Please enter a reason for monitoring this ride.',
        });
        return;
      }

      // - Regular taxi providers like Uber and Lyft can be ordered with any Ride Type (all)
      // - Local Taxi Vendors don't have Ride Type selection (Single, Repeated, Appointment, Will Call)
      // - Stawav don't have some Ride Type options available (Appointment, Will Call)

      if (isFresnoEOCTaxi.value) {
        return status.isShowFresnoEstimation ? createLocalTaxiScheduledRide() : getFresnoEOCEstimation();
      }

      if (isGreenPointTaxi.value) {
        return status.isShowGreenPointEstimation ? createLocalTaxiScheduledRide() : getGreenPointEstimation();
      }

      if (isLocalTaxiRideType.value) {
        const partnerLocalTaxiRideType = findPartnerLocalTaxiRideType(customerPartnerCode.value, rideData.value.rideType);

        if (partnerLocalTaxiRideType?.data?.partnerTaxiVendor?.applySubsidyCondition) {
          const resultMedicalTrip = await checkMedicalTripForPartnerships(true);
          if (!resultMedicalTrip) {
            status.isLoading = false;
            return;
          }
        }

        return createLocalTaxiScheduledRide();
      }

      if (isStawavRideType.value) {
        return status.isShowStawavEstimation ? createOrUpdateStawavScheduledRide() : getStawavEstimation();
      }

      status.resultHtml = '';
      status.isLoading = true;

      if (isAfterGettingEstimation.value || !isGetEstimateNeeded.value) {
        createUpdateRide();
      } else {
        status.preferredContactWarning = '';

        await getRideCallerInfo();

        // if there is no caller with ride phone number, no need to move forward
        if (!rideCaller.value) {
          status.isLoading = false;
          return;
        }

        if (rideData.value.phoneNumber !== rideData.value.riderPhone) {
          checkPreferredNumber();
        }

        const activeRide = await getActiveRide(rideCaller.value.id);

        if (activeRide) {
          const isConfirmedToCancelActiveRide = await showConfirmPopupDialog({
            text: 'The rider has an active ride would you like to cancel this ride?',
            title: 'Confirm with Client?',
            confirmButtonText: 'Yes, cancel',
          });

          if (isConfirmedToCancelActiveRide) {
            await cancelActiveRide(activeRide.requestId!);
          }
        }

        const resultMedicalTrip = await checkMedicalTripForPartnerships();
        if (!resultMedicalTrip) {
          return;
        }

        const isBirthdayConfirmed = await checkAndUpdateCustomerBirthday();
        if (!isBirthdayConfirmed) {
          status.isLoading = false;
          return;
        }

        const response = await checkServiceTypeQuestions();

        if (!response) {
          status.isLoading = false;
          return;
        }

        getEstimation();
      }
    };

    const getRideCallerInfo = async () => {
      rideCaller.value = null;

      const isScheduledRideCustomerSameAsRightPanelCustomer =
        customerInfo.value.id && rideData.value.phoneNumber === customerPhoneNumber.value;

      if (isScheduledRideCustomerSameAsRightPanelCustomer) {
        rideCaller.value = _.cloneDeep(customerInfo.value);
      } else {
        const loading = ElLoading.service({
          lock: true,
          text: 'Loading Customer Info...',
          background: 'rgba(0, 0, 0, 0.7)',
          customClass: 'custom-full-spinner-class',
        });

        try {
          rideCaller.value = await callerService.fetchCustomerInfo({
            phoneNumber: rideData.value.phoneNumber,
            partnerId: partnerId.value || null,
          });
        } catch (error) {
          status.resultHtml = util.getGraphqlError(error);
        }

        loading.close();
      }
    };

    const getActiveRide = async (callerId: string) => {
      const loading = ElLoading.service({
        lock: true,
        text: 'Loading Active Ride...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      let activeRide = null;
      try {
        activeRide = await rideService.getActiveRide(callerId);
      } catch (error) {
        console.log(error);
      } finally {
        loading.close();
      }

      return activeRide;
    };

    const cancelActiveRide = async (requestId: string) => {
      const data = {
        requestId,
        opCancellationReason: 'Rider wants to schedule a ride instead',
      };

      const loading = ElLoading.service({
        lock: true,
        text: 'Canceling Active Ride...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      try {
        await sendDelete('rides', {
          data,
        });

        showSuccessPopupDialog({
          text: 'The active ride has been cancelled successfully!',
        });
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      } finally {
        loading.close();
      }
    };

    const checkPreferredNumber = async () => {
      try {
        const response = await callerService.getCallerLovedOneIdName(rideData.value.riderPhone);

        status.preferredContactWarning = util.checkAndGetBelongsWarningMessage(rideCaller.value, response);

        if (response) {
          showWarningPopupDialog({
            text: 'Please make sure this is the phone number the user wants for this ride!',
            title: status.preferredContactWarning,
          });
        }
      } catch (error) {
        console.log(error);
      }
    };

    const getRideDateLocal = () => {
      if (rideData.value.scheduleKind === 'appointment') {
        const guessedRideDuration = 15; // assume the ride takes 15 minutes for better adjustement to traffic
        const { scheduledAppointmentBufferMinutes, scheduledAppointmentTime, scheduledAppointmentDate } = rideData.value;
        const diffMinutes = (scheduledAppointmentBufferMinutes || 0) + guessedRideDuration;

        const pickupTime = Moment(scheduledAppointmentTime).subtract(diffMinutes, 'minutes').format('hh:mm A');
        return `${util.formatDateTime(scheduledAppointmentDate, 'YYYY-MM-DD')} ${pickupTime}`;
      }

      const pickupTime = Moment(rideData.value.pickupTime).format('hh:mm A');

      if (scheduledRideId.value && rideData.value.rideMultiple && rideData.value.nextPickupDate) {
        return `${util.formatDateTime(rideData.value.nextPickupDate, 'YYYY-MM-DD')} ${pickupTime}`;
      }

      return `${util.formatDateTime(rideData.value.pickupDate, 'YYYY-MM-DD')} ${pickupTime}`;
    };

    const getEstimationPostData = () => {
      trimAddress(startAddress.value);
      trimAddress(endAddress.value);
      const startStreetAddress = getStreetAddressByType(startAddress.value);
      const endStreetAddress = getStreetAddressByType(endAddress.value);

      const postData: any = rideUtility.makeEstimationPostData(
        {
          ...rideData.value,
          typeOfCar: rideData.value.rideType,
          startAddress: startAddress.value,
          endAddress: endAddress.value,
          isBusinessAccount: isBusinessAccount.value,
          rideDateLocal: getRideDateLocal(),
          pickupDropoffNotes: pickupDropoffNotes.value,
        },
        startStreetAddress,
        endStreetAddress,
        false,
      );

      if (isTransportation.value) {
        postData.isDoNotApplySubsidy = rideData.value.isDoNotApplySubsidy ? 1 : 0;
        postData.isDoNotApplyExpiringCredits = rideData.value.isDoNotApplyExpiringCredits ? 1 : 0;
      }

      if (markers.value[1].isShow) {
        postData.endCoors = {
          lat: endAddress.value.latitude,
          lng: endAddress.value.longitude,
        };
      }

      if (rideData.value.scheduleKind === 'willcall') {
        postData.isWillCall = true;
      }

      postData.overrideActiveRideCheck = true;
      postData.isChargeRideOperatingFee = false;

      return postData;
    };

    const applyEstimationData = async (estimationData: any, { showWarnings = true }: { showWarnings?: boolean } = {}) => {
      estimationRide.value = rideUtility.parseEstimationRide({
        estimationData,
      });

      status.isShowNormalRideEstimation = true;

      if (rideData.value.scheduleKind === 'appointment') {
        const { scheduledAppointmentBufferMinutes, scheduledAppointmentTime, scheduledAppointmentDate } = rideData.value;
        const diffMinutes = (scheduledAppointmentBufferMinutes || 0) + estimationRide.value.duration;
        rideData.value.pickupTime = Moment(scheduledAppointmentTime).subtract(diffMinutes, 'minutes');
        rideData.value.pickupDate = scheduledAppointmentDate;
      }

      if (!showWarnings) {
        if (estimationRide.value.warningMessage) {
          const htmlEstimationWarning = util.convertLineBreakTextIntoBulletPointsHtml(estimationRide.value.warningMessage);
          await showWarningPopupDialog({
            text: htmlEstimationWarning,
            type: 'html',
          });
        }
        return;
      }

      await showReturnTripAlertIfNeeded(estimationRide.value.isNoReturnTrip, estimationRide.value.returnTripEtaMins);

      if (isPhoneNumberValid.value) {
        await checkSimilarRide();
      }

      if (estimationRide.value.warningMessage) {
        const htmlEstimationWarning = util.convertLineBreakTextIntoBulletPointsHtml(estimationRide.value.warningMessage);
        await showWarningPopupDialog({
          text: htmlEstimationWarning,
          type: 'html',
        });
      }

      if (estimationData.status === 200 && estimationData.eta >= 15) {
        showPopupToChangePickupTime();
      }

      await showLowDriverAvailabilityMessage(estimationRide.value.fulfillmentIndicator);
    };

    const setPartnerEstimatesFromResponse = (data: any) => {
      const partnerEstimates = data.allPartnerEstimates;
      defaultEstimateData.value = data;
      allPartnerEstimates.value = Array.isArray(partnerEstimates) ? partnerEstimates : [];

      if (allPartnerEstimates.value.length > 0) {
        selectedPartnerEstimateIndex.value = 1;
        return allPartnerEstimates.value[0];
      }

      selectedPartnerEstimateIndex.value = 0;
      return data;
    };

    const selectPartnerEstimateTab = async (index: number) => {
      if (index === selectedPartnerEstimateIndex.value) {
        return;
      }

      if (index === 0) {
        if (!defaultEstimateData.value) {
          return;
        }
        selectedPartnerEstimateIndex.value = 0;
        operatorConfirmedSurgePriceWithCustomer.value = false;
        await applyEstimationData(defaultEstimateData.value, { showWarnings: false });
        return;
      }

      const partnerEstimate = allPartnerEstimates.value[index - 1];
      if (!partnerEstimate) {
        return;
      }

      selectedPartnerEstimateIndex.value = index;
      operatorConfirmedSurgePriceWithCustomer.value = false;
      await applyEstimationData(partnerEstimate, { showWarnings: false });
    };

    const getEstimation = async () => {
      const postData = getEstimationPostData();

      const loading = ElLoading.service({
        lock: true,
        text: 'Getting Estimation...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      try {
        const response = await sendPost('getWebEstimate', postData);
        const estimationData = setPartnerEstimatesFromResponse(response.data);

        await applyEstimationData(estimationData);
      } catch (error) {
        status.resultHtml = util.getErrorResponse(error);
        status.isShowNormalRideEstimation = true;
      }
      status.isLoading = false;
      loading.close();
    };

    const showPopupToChangePickupTime = async () => {
      const newPickupTime = Moment(rideData.value.pickupTime).subtract(estimationRide.value.eta, 'minutes').toDate();
      const formattedTime = util.formatDateTime(newPickupTime, 'hh:mm A');
      const questionText = `
        It looks drivers in the area are far away from this scheduled ride's pick up location.
        Please ask the client if they would like to schedule their pick up at ${formattedTime} to give the driver more time to get to them.
      `;

      const isChangePickupTimeConfirmed = await showConfirmPopupDialog({
        text: questionText,
        title: 'Confirm with Client',
        confirmButtonText: 'Confirmed',
      });

      if (!isChangePickupTimeConfirmed) {
        return;
      }

      rideData.value.pickupTime = newPickupTime;
    };

    const checkSimilarRide = async () => {
      let currentRideDateTime = util.formatDateTime(rideData.value.pickupDate, 'YYYY-MM-DD');
      currentRideDateTime = `${currentRideDateTime} ${util.formatDateTime(rideData.value.pickupTime, 'hh:mm:ss')}`;
      similarScheduledRide.value = null;

      const loading = ElLoading.service({
        lock: true,
        text: 'Checking Similar Scheduled Ride...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      try {
        const response = await scheduledRideService.fetchScheduledRideRecords({
          phoneNumber: rideData.value.phoneNumber,
        });

        for (const scheduledRide of response.rows) {
          if (scheduledRideId.value && scheduledRideId.value === scheduledRide.id) {
            continue;
          }

          if (!scheduledRide.completedAt && !scheduledRide.canceledAt && !similarScheduledRide.value) {
            const pickupDateTime = util.formatDateTime(scheduledRide.firstPickup, 'YYYY-MM-DD HH:mm:ss');
            const durationMins = Math.abs(Moment.duration(Moment(currentRideDateTime).diff(Moment(pickupDateTime))).asMinutes());

            if (durationMins < similarScheduledRideLimitDurationMins) {
              similarScheduledRide.value = _.cloneDeep(scheduledRide);
              break;
            }
          }
        }
      } catch (error) {
        console.log(error);
      }
      loading.close();
    };

    const createUpdateRide = async () => {
      if (unknownNameList.indexOf(rideData.value.rideName.toLowerCase()) !== -1) {
        showErrorPopupDialog({
          text: `Cannot use { ${rideData.value.rideName} } for the trip name. Please enter a trip name that the rider can remember easily.`,
        });
        status.isLoading = false;
        return;
      }

      const postData = getScheduledRidePostData();

      const loading = ElLoading.service({
        lock: true,
        text: !scheduledRideId.value ? 'Creating Ride Scheduler...' : 'Updating Ride Scheduler...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      if (!scheduledRideId.value) {
        // Create Ride Scheduler
        try {
          const disallowVendorScheduledRide = rideData.value.scheduleKind === 'appointment' || rideData.value.serviceType !== RideServiceType.Transportation;

          const response = !rideData.value.isWillCall
            ? await scheduledRideService.createScheduledRide(postData, disallowVendorScheduledRide)
            : await scheduledRideService.createWillCallRide(postData);

          status.isLoading = false;
          loading.close();

          await showSuccessPopupDialog({
            text: `Your ${rideData.value.isWillCall ? '"Will Call"' : 'scheduled'} ride has been created. Confirmation number: ${response.id}`,
          });

          resetForm();
        } catch (error) {
          status.resultHtml = util.getGraphqlError(error);
          status.isShowNormalRideEstimation = false;
          status.isLoading = false;
          loading.close();
        }
      } else {
        // Update Ride Scheduler
        try {
          await scheduledRideService.updateScheduledRide({
            scheduledRideId: scheduledRideId.value,
            data: postData,
          });

          status.isLoading = false;
          loading.close();

          await showSuccessPopupDialog({
            text: `Your scheduled ride has been updated. Confirmation number: ${scheduledRideId.value}`,
          });

          resetForm();
        } catch (error) {
          status.resultHtml = util.getGraphqlError(error);
          status.isShowNormalRideEstimation = false;
          status.isLoading = false;
          loading.close();
        }
      }
    };

    const createLocalTaxiScheduledRide = async () => {
      const loading = ElLoading.service({
        lock: true,
        text: 'Creating Local Taxi Scheduled Ride...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      status.resultHtml = '';
      try {
        await scheduledRideService.createLocalTaxiScheduledRide({
          phoneNumber: rideData.value.phoneNumber,
          pickupDate: util.formatDateTime(localTaxiRideData.value.pickupDate),
          pickupTime: util.formatDateTime(localTaxiRideData.value.pickupTime, 'hh:mm A'),
          returnTime: localTaxiRideData.value.returnTime ? util.formatDateTime(localTaxiRideData.value.returnTime, 'hh:mm A') : null,
          pickupAddress: getFormattedAddress(startAddress.value),
          dropoffAddress: getFormattedAddress(endAddress.value),
          rideType: rideData.value.rideType,
          notes: localTaxiRideData.value.noteToTaxiCompany,
          isMedicalTrip: rideData.value.isMedicalTrip,
          partnerRidePurpose: rideData.value.partnerRidePurpose,
        });

        showSuccessPopupDialog({
          text: 'Local taxi scheduled ride has been created successfully!',
        });

        status.isShowFresnoEstimation = false;
        status.isShowGreenPointEstimation = false;
      } catch (error) {
        status.resultHtml = util.getGraphqlError(error);
      }
      loading.close();
    };

    const getStawavEstimation = async () => {
      const payload = getStawavRideData({
        startAddress: startAddress.value,
        endAddress: endAddress.value,
        partnerId: rideData.value.partnerId || customerPartnerId.value,
      });

      const loading = ElLoading.service({
        lock: true,
        text: 'Getting Stawav Scheduled Ride Estimation...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      status.resultHtml = '';
      try {
        const response = await scheduledRideService.getStaWavScheduledRideEstimation(payload);

        stawavEstimation.value.wheelchairTransportationRate = response.wheelchairTransportationRate;
        stawavEstimation.value.wheelchairTransportationStandardRate = response.wheelchairTransportationStandardRate;
        stawavEstimation.value.wheelchairTransportationSurchargeRate = response.wheelchairTransportationSurchargeRate;
        stawavEstimation.value.passengerTransportationRate = response.passengerTransportationRate;
        stawavEstimation.value.passengerTransportationStandardRate = response.passengerTransportationStandardRate;
        stawavEstimation.value.passengerTransportationSurchargeRate = response.passengerTransportationSurchargeRate;
        stawavEstimation.value.waitTimeRate = response.waitTimeRate;

        status.isShowStawavEstimation = true;
      } catch (error) {
        status.resultHtml = util.getGraphqlError(error);
        status.isShowStawavEstimation = false;
      }
      loading.close();
    };

    const createOrUpdateStawavScheduledRide = async () => {
      const payload = getStawavRideData({
        startAddress: startAddress.value,
        endAddress: endAddress.value,
        partnerId: rideData.value.partnerId || customerPartnerId.value,
      });

      const loading = ElLoading.service({
        lock: true,
        text: scheduledRideId.value ? 'Updating StaWav Scheduled Ride...' : 'Creating StaWav Scheduled Ride...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      status.resultHtml = '';
      let response: ScheduledRide;
      try {
        if (scheduledRideId.value) {
          response = await scheduledRideService.replaceStaWavScheduledRide({
            data: payload,
            scheduledRideId: scheduledRideId.value,
          })
        } else {
          response = await scheduledRideService.createStaWavScheduledRide({
          data: payload,
        });
        }

        showSuccessPopupDialog({
          text: `Your scheduled ride has been ${scheduledRideId.value ? 'updated' : 'created'}. Confirmation number: ${response.id}`,
        });

        resetForm();
      } catch (error) {
        status.resultHtml = util.getGraphqlError(error);
      }
      loading.close();
    };

    const getFresnoEOCEstimation = async () => {
      const isIn24Hrs = await showConfirmPopupDialog({
        title: 'Is this trip request 24 hrs in advance?',
      });

      if (!isIn24Hrs) {
        return;
      }

      const isRideInFresnoArea = await showConfirmPopupDialog({
        title: 'Is this trip within the Fresno or Clovis area only?',
      });

      if (!isRideInFresnoArea) {
        return;
      }

      const distance = getDistance(
        startAddress.value.latitude!,
        startAddress.value.longitude!,
        endAddress.value.latitude!,
        endAddress.value.longitude!,
      );
      fresnoEstimation.value.distance = distance;

      if (distance <= 3) {
        fresnoEstimation.value.rate = 10;
      } else if (distance <= 5) {
        fresnoEstimation.value.rate = 5;
      } else if (distance <= 10) {
        fresnoEstimation.value.rate = 20;
      } else {
        fresnoEstimation.value.rate = 20 + (distance - FRESNO_DISTANCE_LIMIT) * FRESNO_ADDITION_DISTANCE_RATE;
      }

      status.isShowFresnoEstimation = true;
    };

    const getGreenPointEstimation = async () => {
      const isConfirmedSameDay = await showConfirmPopupDialog({
        title: 'Confirmed Same Day Request & Within Reedley',
      });

      if (!isConfirmedSameDay) {
        return;
      }

      const isConfirmedWaitTime = await showConfirmPopupDialog({
        text: 'Note: Rides must start from Reedley and the customer can be taken anywhere they need to',
        title: 'Confirmed 48 Hrs Notice with Possible Wait Times',
      });

      if (!isConfirmedWaitTime) {
        return;
      }

      const distance = getDistance(
        startAddress.value.latitude!,
        startAddress.value.longitude!,
        endAddress.value.latitude!,
        endAddress.value.longitude!,
      );
      fresnoEstimation.value.distance = distance;
      fresnoEstimation.value.rate = Number((3.4 + 2.85 * distance).toFixed(2));

      status.isShowGreenPointEstimation = true;
    };

    const showLookingDriverBefore10MinsPopup = () => {
      if (!rideData.value.pickupTime) {
        return;
      }

      if (status.isShowLookingDriverBefore10MinsPopup) {
        return;
      }

      const title = isVendorScheduledRide.value ? 'Vendor Scheduled Ride' : 'Please Remind Customer';

      const text = isVendorScheduledRide.value
        ? 'This ride will be scheduled directly with Uber or Lyft to dispatch a driver in time for pickup.'
        : "We start looking for a driver 10 minutes before pick up time and will call once a driver's dispatched.";

      showWarningPopupDialog({
        text,
        title,
      });

      status.isShowLookingDriverBefore10MinsPopup = true;
    };

    const resetForm = async () => {
      rideDataOriginal.value = null;
      rideData.value = {
        customRiderName: '',
        phoneNumber: rideAvailablePhoneNumber.value || '',
        riderPhone: '',
        callToConfirmSurge: '',
        rideName: '',
        partnerRidePurpose: '',
        rideType: getAvailableRideType({
          rideTypes: availableRideTypes.value,
          partnerCode: customerPartnerCode.value,
          customerInfo: customerInfo.value,
        }),
        serviceType: RideServiceType.Transportation,
        scheduleKind: 'single',
        rideMultiple: false,
        isWillCall: false,
        pickupDate: null,
        pickupTime: null,
        scheduledAppointmentDate: null,
        scheduledAppointmentTime: null,
        scheduledAppointmentBufferMinutes: 10,
        repeatEveryXWeek: 1,
        day0: false,
        day1: false,
        day2: false,
        day3: false,
        day4: false,
        day5: false,
        day6: false,
        endOn: null,
        endOnType: 'never',
        endAfterXTimes: 0,
        noteToDriver: '',
        notesForLP: '',
        notesForSpecialRequestOrPharmacy: '',
        minutesBeforeCustomReminder: '',
        minutesAfterCreationCustomReminder: customerPartner?.value?.callOptions?.scheduledRideReminderMinutesAfterCreation || '',
        isDoNotApplySubsidy: false,
        isDoNotApplyExpiringCredits: false,
        overrideBigCar: false,
        overrideSmallCar: false,
        overrideBannedCar: false,
        overrideBadDriver: false,
        isExpandDriverSearch: customerInfo.value?.isExpandDriverSearch || false,
        isExpandVehicleSearch: customerInfo.value?.isExpandVehicleSearch || false,
        gender: genderList[0].key,
        levelOfService: stawavLevelOfServices[0].key,
        hasOwnWheelchair: false,
        specialInstructions: '',
        numberOfCompanions: 0,
        appointmentType: '',
        appointmentTime: '',
        returnTime: '',
        stairStepsForPU: 0,
        stairStepsForDO: 0,
        dropOffPhoneNumber: '',
        isMedicalTrip: false,
      };

      startAddressOriginal.value = null;
      startAddress.value = {
        streetAddress: '',
        streetAddress1: '',
        city: '',
        state: '',
        zip: '',
        name: '',
        latitude: null,
        longitude: null,
        type: false,
      };

      endAddressOriginal.value = null;
      endAddress.value = {
        streetAddress: '',
        streetAddress1: '',
        city: '',
        state: '',
        zip: '',
        name: '',
        latitude: null,
        longitude: null,
        type: false,
      };

      localTaxiRideData.value = {
        pickupDate: '',
        pickupTime: '',
        noteToTaxiCompany: '',
        returnTime: '',
      };

      driverNotePickUp.value = '';
      driverNoteDropOff.value = '';
      isNeededToMonitorRide.value = false;
      status.isShowStawavEstimation = false;
      status.isShowFresnoEstimation = false;
      status.isShowGreenPointEstimation = false;
      status.isShowNormalRideEstimation = false;
      status.isShowLookingDriverBefore10MinsPopup = false;

      scheduledRideId.value = 0;

      if (customerPhoneNumber.value) {
        status.isExistPhoneNumber = true;
      } else {
        status.isExistPhoneNumber = false;
      }

      router.push({
        path: route.path,
        query: {
          phoneNumber: customerPhoneNumber.value,
        },
      });
    };

    const cancelEstimation = () => {
      status.resultHtml = '';
      similarScheduledRide.value = null;
      status.isShowStawavEstimation = false;
      status.isShowFresnoEstimation = false;
      status.isShowGreenPointEstimation = false;
      status.isShowNormalRideEstimation = false;
      defaultEstimateData.value = null;
      allPartnerEstimates.value = [];
      selectedPartnerEstimateIndex.value = 0;
    };

    const setLocalTaxiDisabledState = (isDisabled: boolean) => {
      isLocalTaxiNoteDisabled.value = isDisabled;
    };

    watch(driverArrivals, () => {
      driverArrival.value = driverArrivals.value[0].key;
    });

    watch([pickupVenueNote, riderClothes, driverArrival], () => {
      driverNotePickUp.value = [
        pickupVenueNote.value && `🚪 ${pickupVenueNote.value}`,
        riderClothes.value && `👚 Look for: ${riderClothes.value}`,
        driverArrival.value && `☎️ ${driverArrival.value}`,
      ].filter(Boolean).join('\n');
    });

    watch(hasGroceries, (value: boolean) => {
      if (value) {
        rideData.value.noteToDriver += rideData.value.noteToDriver ? ' ' : '';
        rideData.value.noteToDriver += 'The rider has groceries.';
      } else {
        const pattern = /The rider has groceries./gi;
        rideData.value.noteToDriver = rideData.value.noteToDriver?.replace(pattern, '');
      }
    });

    watch(hasLuggage, (value: boolean) => {
      if (value) {
        rideData.value.noteToDriver += rideData.value.noteToDriver ? ' ' : '';
        rideData.value.noteToDriver += 'The rider has luggage.';
      } else {
        const pattern = /The rider has luggage./gi;
        rideData.value.noteToDriver = rideData.value.noteToDriver?.replace(pattern, '');
      }
    });

    watch(customerInfo, (value: any, oldValue: any) => {
      if (value && oldValue && value.id && value.id === oldValue.id) {
        return;
      }

      resetForm();
    });

    watch(
      availableRideTypes,
      () => {
        rideData.value.rideType = getAvailableRideType({
          rideTypes: availableRideTypes.value,
          partnerCode: customerPartnerCode.value,
          customerInfo: customerInfo.value,
        });
      },
      {
        immediate: true,
      },
    );

    watch(rideData, (rs) => {
      if (rs.rideMultiple) {
        rideData.value.scheduleKind = 'multi'
      } else if (rs.scheduledAppointmentDate) {
        rideData.value.scheduleKind = 'appointment'
      } else if (rs.isWillCall) {
        rideData.value.scheduleKind = 'willcall'
      } else {
        rideData.value.scheduleKind = 'single'
      }
    });

    watch(
      () => rideData.value.scheduleKind,
      (v) => {
        switch (v) {
          case 'multi':
            return (rideData.value.rideMultiple = true);
          case 'willcall':
            rideData.value.rideMultiple = false;
            rideData.value.isWillCall = true;
            // just set to mid day
            // it doesn't matter in willcall
            // only need to make sure timezone shifts won't move it to a different date
            rideData.value.pickupTime = Moment().hour(12).toDate();
            return;
          default:
            return (rideData.value.rideMultiple = false);
        }
      },
    );

    watch(
      () => rideData.value.endOnType,
      (value: string) => {
        if (value === 'after') {
          rideData.value.endAfterXTimes = rideData.value.endAfterXTimes || 0;
        }
      },
    );

    watch(
      () => rideData.value.pickupTime,
      (value: string) => {
        if (value) {
          const currentTime = util.formatDateTime(value, 'HH:mm') as string;
          if (currentTime.localeCompare('00:00') >= 0 && currentTime.localeCompare('05:00') <= 0) {
            status.isShowTimeWarningMessage = true;
          } else {
            status.isShowTimeWarningMessage = false;
          }
        }
      },
    );

    watch(
      () => rideData.value.isExpandDriverSearch,
      (value: boolean) => {
        rideData.value.overrideBadDriver = !!value;
      },
    );

    watch(
      () => rideData.value.isExpandVehicleSearch,
      (value: boolean) => {
        rideData.value.overrideBigCar = !!value;
        rideData.value.overrideSmallCar = !!value;
        rideData.value.overrideBannedCar = !!value;
      },
    );

    watch(startAddress, async (value: CustomLocationType, oldValue: CustomLocationType) => {
      const chosenEntrance = await getSelectedEntranceCoordinates(partnerCode.value, value);

      if (chosenEntrance) {
        // update pin
        setMarkerPosition(chosenEntrance.lat, chosenEntrance.lng, 0, false, true);
      } else {
        const isAddressChanged = util.isAddressChanged(value, oldValue);
        setMarkerPosition(Number(value.latitude), Number(value.longitude), 0, false, isAddressChanged);
      }
    });

    watch(endAddress, async (value: CustomLocationType, oldValue: CustomLocationType) => {
      const chosenEntrance = await getSelectedEntranceCoordinates(partnerCode.value, value);

      if (chosenEntrance) {
        // update pin
        setMarkerPosition(chosenEntrance.lat, chosenEntrance.lng, 0, false, true);
      } else {
        const isAddressChanged = util.isAddressChanged(value, oldValue);
        setMarkerPosition(Number(value.latitude), Number(value.longitude), 1, false, isAddressChanged);
      }
    });

    watch(isTransportation, (value: boolean) => {
      if (value) {
        return;
      }

      rideData.value.isDoNotApplySubsidy = true;
    });

    watch(isStawavRideType, (value: boolean) => {
      if (!value) {
        return;
      }

      rideData.value.serviceType = RideServiceType.Transportation;
    });

    watch(isLocalTaxiRideType, (value: boolean) => {
      if (!value) {
        return;
      }

      rideData.value.serviceType = RideServiceType.Transportation;
    });

    const isGetEstimateNeeded = ref(true);

    watch(() => [rideData, rideDataOriginal, startAddress, startAddressOriginal, endAddress, endAddressOriginal],
      () => {
        if (!rideDataOriginal.value || !startAddressOriginal.value || !endAddressOriginal.value) {
          isGetEstimateNeeded.value = true;
          return;
        }

        const omitFalsy = (obj: any) => _.omitBy(obj, (val) => !val);

        if (!_.isEqual(omitFalsy(startAddress.value), omitFalsy(startAddressOriginal.value))) {
          isGetEstimateNeeded.value = true;
          return;
        }

        if (!_.isEqual(omitFalsy(endAddress.value), omitFalsy(endAddressOriginal.value))) {
          isGetEstimateNeeded.value = true;
          return;
        }

        const fieldsNotRequiringNewEstimate = [
          'minutesBeforeCustomReminder', 'minutesAfterCreationCustomReminder', 'isExpandDriverSearch', 'isExpandVehicleSearch',
          'noteToDriver', 'notesForLP', 'callToConfirmSurge', 'rideName',
        ];

        for (const [key, value] of Object.entries(rideData.value)) {
          if (fieldsNotRequiringNewEstimate.includes(key)) {
            continue;
          }
          if (!_.isEqual(value, rideDataOriginal.value[key])) {
            isGetEstimateNeeded.value = true;
            return;
          }
        }

        isGetEstimateNeeded.value = false;
      },
      { deep: true },
    );

    onMounted(() => {
      util.moveToTop();

      scheduledRideId.value = route.query.scheduledRideId ? Number(route.query.scheduledRideId as string) : 0;
      status.isExistPhoneNumber = route.query.phoneNumber ? true : false;
      const rideId = (route.query.rideId as string) || '';

      if (scheduledRideId.value) {
        getRideScheduler();
      } else if (rideId) {
        getRideById(rideId);
      } else if (duplicatedScheduledRide.value) {
        const data = rideUtility.getRideSchedulerData(duplicatedScheduledRide.value);
        startAddress.value = _.cloneDeep(data.startAddress);
        endAddress.value = _.cloneDeep(data.endAddress);
        rideDataOriginal.value = _.omit(data, ['startAddress', 'endAddress']);
        rideData.value = _.cloneDeep(rideDataOriginal.value);
        isNeededToMonitorRide.value = !!rideData.value.notesForLP;

        setDuplicatedScheduledRide(null);
        rideData.value.pickupDate = null;
        rideData.value.pickupTime = null;
      } else if (rideOrderData.value) {
        rideDataOriginal.value = {
          customRiderName: rideOrderData.value.customRiderName || '',
          phoneNumber: rideOrderData.value.phoneNumber || '',
          riderPhone: rideOrderData.value.riderPhone || '',
          callToConfirmSurge: rideOrderData.value.callToConfirmSurge || '',
          rideName: rideOrderData.value.rideName || '',
          partnerRidePurpose: rideOrderData.value.partnerRidePurpose || '',
          rideType: getAvailableRideType({
            rideType: rideOrderData.value.typeOfCar,
            rideTypes: availableRideTypes.value,
            partnerCode: customerPartnerCode.value,
            customerInfo: customerInfo.value,
          }),
          serviceType: rideOrderData.value.serviceType || RideServiceType.Transportation,
          rideMultiple: rideOrderData.value.rideMultiple || false,
          isWillCall: rideOrderData.value.isWillCall || false,
          pickupDate: rideOrderData.value.pickupDate || null,
          pickupTime: rideOrderData.value.pickupTime || null,
          repeatEveryXWeek: rideOrderData.value.repeatEveryXWeek || 1,
          day0: rideOrderData.value.day0 || false,
          day1: rideOrderData.value.day1 || false,
          day2: rideOrderData.value.day2 || false,
          day3: rideOrderData.value.day3 || false,
          day4: rideOrderData.value.day4 || false,
          day5: rideOrderData.value.day5 || false,
          day6: rideOrderData.value.day6 || false,
          endOn: rideOrderData.value.endOn || null,
          endOnType: rideOrderData.value.endOnType || 'never',
          endAfterXTimes: rideOrderData.value.endAfterXTimes || 0,
          noteToDriver: rideOrderData.value.noteToDriver || '',
          minutesBeforeCustomReminder: rideOrderData.value.minutesBeforeCustomReminder || '',
          minutesAfterCreationCustomReminder:
            rideOrderData.value.minutesAfterCreationCustomReminder !== undefined
              ? rideOrderData.value.minutesAfterCreationCustomReminder
              : customerPartner?.value?.callOptions?.scheduledRideReminderMinutesAfterCreation,
          overrideBigCar: rideOrderData.value.rideType?.overrideBigCar || false,
          overrideSmallCar: rideOrderData.value.rideType?.overrideSmallCar || false,
          overrideBannedCar: rideOrderData.value.rideType?.overrideBannedCar || false,
          overrideBadDriver: rideOrderData.value.rideType?.badDrivers || false,
          isDoNotApplySubsidy: rideOrderData.value.rideType?.isDoNotApplySubsidy || false,
          isDoNotApplyExpiringCredits: rideOrderData.value.rideType?.isDoNotApplyExpiringCredits || false,
          isExpandDriverSearch: rideOrderData.value.isExpandDriverSearch || false,
          isExpandVehicleSearch: rideOrderData.value.isExpandVehicleSearch || false,
          notesForLP: rideOrderData.value.notesForLP || '',
          gender: genderList[0].key,
          levelOfService: stawavLevelOfServices[0].key,
          hasOwnWheelchair: false,
          specialInstructions: '',
          numberOfCompanions: 0,
          appointmentType: '',
          appointmentTime: '',
          returnTime: '',
          stairStepsForPU: 0,
          stairStepsForDO: 0,
          dropOffPhoneNumber: '',
        };
        rideData.value = _.cloneDeep(rideDataOriginal.value);
        isNeededToMonitorRide.value = !!rideOrderData.value.isNeededToMonitorRide;

        startAddressOriginal.value = rideOrderData.value.startAddress
          ? _.cloneDeep(rideOrderData.value.startAddress)
          : {
              streetAddress: '',
              streetAddress1: '',
              city: '',
              state: '',
              zip: '',
              name: '',
              latitude: null,
              longitude: null,
              type: false,
            };
        startAddress.value = _.cloneDeep(startAddressOriginal.value);

        endAddressOriginal.value = rideOrderData.value.endAddress
          ? _.cloneDeep(rideOrderData.value.endAddress)
          : {
              streetAddress: '',
              streetAddress1: '',
              city: '',
              state: '',
              zip: '',
              name: '',
              latitude: null,
              longitude: null,
              type: false,
            };
        endAddress.value = _.cloneDeep(endAddressOriginal.value);

        driverNotePickUp.value = rideOrderData.value.driverNotePickUp ?? '';
        driverNoteDropOff.value = rideOrderData.value.driverNoteDropOff ?? '';
      } else {
        const startDate = route.query.startDate ? Moment(route.query.startDate as string).toDate() : null;
        rideData.value.phoneNumber = rideAvailablePhoneNumber.value || '';
        rideData.value.pickupDate = startDate || rideData.value.pickupDate;
      }
    });

    onBeforeUnmount(() => {
      const newRideOrderData = {
        ...rideOrderData.value,
        customRiderName: rideData.value.customRiderName,
        phoneNumber: rideData.value.phoneNumber,
        riderPhone: rideData.value.riderPhone,
        rideName: rideData.value.rideName,
        typeOfCar: rideData.value.rideType,
        serviceType: rideData.value.serviceType,
        partnerRidePurpose: rideData.value.partnerRidePurpose,
        minutesBeforeCustomReminder: rideData.value.minutesBeforeCustomReminder,
        minutesAfterCreationCustomReminder: rideData.value.minutesAfterCreationCustomReminder,
        rideType: {
          badDrivers: rideData.value.overrideBadDriver,
          overrideBigCar: rideData.value.overrideBigCar,
          overrideSmallCar: rideData.value.overrideSmallCar,
          overrideBannedCar: rideData.value.overrideBannedCar,
          isDoNotApplySubsidy: rideData.value.isDoNotApplySubsidy,
          isDoNotApplyExpiringCredits: rideData.value.isDoNotApplyExpiringCredits,
        },
        rideMultiple: rideData.value.rideMultiple,
        isWillCall: rideData.value.isWillCall,
        pickupDate: rideData.value.pickupDate,
        pickupTime: rideData.value.pickupTime,
        repeatEveryXWeek: rideData.value.repeatEveryXWeek,
        day0: rideData.value.day0,
        day1: rideData.value.day1,
        day2: rideData.value.day2,
        day3: rideData.value.day3,
        day4: rideData.value.day4,
        day5: rideData.value.day5,
        day6: rideData.value.day6,
        endOnType: rideData.value.endOnType,
        endOn: rideData.value.endOn,
        endAfterXTimes: rideData.value.endAfterXTimes,
        noteToDriver: rideData.value.noteToDriver,
        notesForLP: rideData.value.notesForLP,
        isNeededToMonitorRide: isNeededToMonitorRide.value,
        callToConfirmSurge: rideData.value.callToConfirmSurge,
        startAddress: startAddress.value,
        endAddress: endAddress.value,
        isExpandDriverSearch: rideData.value.isExpandDriverSearch,
        isExpandVehicleSearch: rideData.value.isExpandVehicleSearch,
        driverNotePickUp: driverNotePickUp.value,
        driverNoteDropOff: driverNoteDropOff.value,
      };

      setRideOrderData(newRideOrderData);
    });

    return {
      pageId,
      availableRideTypes,
      preferredVendorRideTypes,
      customerInfo,
      rideData,
      similarScheduledRide,
      startAddress,
      pickupVenues,
      endAddress,
      dropoffVenues,
      markers,
      scheduledRideId,
      status,
      estimationRide,
      allPartnerEstimates,
      partnerEstimateTabs,
      selectedPartnerEstimateIndex,
      selectedPartnerEstimate,
      selectPartnerEstimateTab,
      startAddressComponent,
      endAddressComponent,
      isPhoneNumberValid,
      estimationBtnText,
      submitBtnText,
      isSubmitBtnDisabled,
      formTitle,
      isPhoneNumberFieldDisabled,
      isRepeatedRide,
      disabledDate,
      isBusinessAccount,
      localTaxiRideData,
      isGeneralRideType,
      availableServiceTypeList,
      serviceTypeOptions,
      isTransportation,
      isLocalTaxiRideType,
      isStawavRideType,
      genderList,
      stawavLevelOfServices,
      stawavEstimation,
      isAfterGettingEstimation,
      fresnoEstimation,
      startAddressAnchorBound,
      endAddressAnchorBound,
      isAllowedToExpandVehicleSearch,
      invalidErrorMessage,
      availablePartnerRidePurposes,
      isScheduledRideRestricted,

      validateScheduledRideReminderMinutesAfterCreation,
      scheduledRideReminderInputElement,
      util,
      swapAddress,
      setMarkerPosition,
      submitForm,
      resetForm,
      cancelEstimation,
      showLookingDriverBefore10MinsPopup,
      shouldDisplaySurge,
      setLocalTaxiDisabledState,
      isGetEstimateNeeded,
      riderClothes,
      driverArrival,
      driverArrivals,
      hasGroceries,
      hasLuggage,
      isNeededToMonitorRide,
      driverNotePickUp,
      driverNoteDropOff,
      setVenuePlaces,
      setVenueNote,
      showAccessOptionsPopupDialog,
      saveAccessibilityOptionsPopupResponse,
      isHideRideEstimateFullDetail,
      operatorConfirmedSurgePriceWithCustomer,
    };
  },
};
</script>

<style lang="scss">
.custom-tooltip.auto-retry {
  width: 400px !important;
  line-height: 20px;

  span {
    color: #e9ec0f;
    font-weight: bold;
  }
}

.ride-scheduler.page-content {
  .scheduled-ride-restriction-alert.alert-danger {
    background-color: #dc3545;
  }

  .partner-estimate-header {
    max-width: 500px;
    margin: 0 auto 12px;
  }

  .partner-estimate-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0;
    margin-bottom: 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  }

  .partner-estimate-tab {
    padding: 8px 14px;
    font-size: 14px;
    font-weight: 500;
    color: #666;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    cursor: pointer;

    &:hover {
      color: #6d509c;
    }

    &--active {
      color: #6d509c;
      font-weight: 700;
      border-bottom-color: #6d509c;
    }
  }

  .partner-estimate-info {
    font-size: 14px;
    border-radius: 5px;
    background: #fff1b3;
    padding: 12px 4px;
  }

  .partner-estimate-info-field {
    margin-bottom: 0;
  }

  .partner-estimate-info-label {
    display: block;
    margin: 0 0 4px;
    padding: 0 12px;
    font-size: 14px;
    font-weight: 700;
  }

  .partner-estimate-info-value {
    padding: 0 12px;
    text-align: left;
    font-size: 14px;
  }

  .mx-datepicker {
    .mx-input-append {
      i {
        font-size: 20px;
      }
    }

    .mx-calendar-header {
      display: none;
    }

    .mx-calendar-content {
      width: 150px;
    }

    .mx-panel-time {
      .mx-time-list {
        width: 50% !important;

        &:last-child {
          display: none;
        }
      }
    }
  }

  .el-date-editor {
    width: 200px !important;
  }

  .pickup-dropoff-notes {
    padding: 5px 10px;
    background: #f4f3ef;
    font-size: 14px;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    color: #000;
  }

  #noteToDriver {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
}
</style>
